import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/browser';
import { ColumnFiltersState } from '@tanstack/react-table';

import {
  Table,
  PageSizeType,
  Sort,
  NativeFilterTypes,
} from 'design-system-web';
import { Text } from 'components/typography';
import { FeatureFlag } from 'constants-flags';
import useFlag from 'lane-shared/hooks/useFlag';

import { useDownloadCSVExport } from './hooks/useDownloadCSVExport';
import { useGetReservableBooking } from '../../hooks/useGetReservableBooking';
import { useGetReservableBookings } from '../../hooks/useGetReservableBookings';
import type {
  DateRange,
  ReservableBookingItem,
} from '../../hooks/useGetReservableBookings';
import { useRowActions } from '../../hooks/useRowActions';
import { useColumnsHeaders } from './hooks/useColumnsHeaders';
import { useReservableTableViewQueryParams } from './hooks/useReservableTableViewQueryParams';
import {
  RowData,
  bookingItemToRowDataMapper,
} from '../../utilities/bookingItemToRowDataMapper';
import { ContentNameCell } from '../ContentNameCell';
import { ReservableStatusDropdown } from '../ReservableStatusDropdown/ReservableStatusDropdown';
import { useExportOptions } from './useExportOptions';
import { SortDirectionEnum } from 'constants-activate';

const TRANSLATION_KEYS = {
  serverError: 'web.admin.channel.reservableManagement.table.serverError',
  channelError: 'web.admin.channel.reservableManagement.table.channelError',
  emptyMessage: 'web.admin.channel.reservableManagement.table.emptyMessage',
  dateRangeFilterLabel:
    'web.admin.channel.reservableManagement.table.dateRangeFilterLabel',
};

const DEFAULT_PAGE_SIZE: PageSizeType = 25;
const DEFAULT_PAGE_START = 0;
const DEFAULT_SORT: Sort = {
  id: 'startDate',
  direction: SortDirectionEnum.Asc,
};

type Props = {
  channelId: string;
  channelTimeZone: string;
};
const DATE_RANGE_FILTER_ID = 'dateFilter';

function isDateRange(filterValue: any): filterValue is DateRange {
  return (
    filterValue &&
    typeof filterValue === 'object' &&
    'startDate' in filterValue &&
    'endDate' in filterValue
  );
}

function getDateRange(filters: ColumnFiltersState) {
  const defaultDateRange = {
    startDate: DateTime.now().startOf('day').toJSDate(),
    endDate: DateTime.now().endOf('day').plus({ day: 30 }).toJSDate(),
  };
  const dateRangeFilter = filters.find(
    filter => filter.id === DATE_RANGE_FILTER_ID
  );

  if (dateRangeFilter && isDateRange(dateRangeFilter.value)) {
    return {
      startDate: dateRangeFilter.value.startDate,
      endDate: dateRangeFilter.value.endDate,
    };
  }

  return defaultDateRange;
}

const noteStyles = {
  width: '220px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
} as CSSProperties;

export function ReservableTableView({ channelId, channelTimeZone }: Props) {
  const [filters, setFilters] = useState<ColumnFiltersState>([]);
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(DEFAULT_PAGE_START);
  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [sort, setSort] = useState(DEFAULT_SORT);

  const { t } = useTranslation();
  const {
    bookings,
    totalBookings,
    isLoading,
    error: getBookingsError,
    fetchBookings,
  } = useGetReservableBookings({
    channelId,
  });
  const { downloadCSVExport, error: downloadCSVError } =
    useDownloadCSVExport(channelId);
  const { initialQueryParams, updateQueryParams } =
    useReservableTableViewQueryParams();
  const hasInitialQueryParamsLoaded = initialQueryParams !== null;
  const isStatusRefreshEnabled = Boolean(
    useFlag(FeatureFlag.ReservableManagementStatusRefresh, false)
  );

  // A status change is persisted server-side, but the rows on screen are a
  // snapshot from the last list fetch. Re-fetch just the updated booking and
  // patch it in, so the row reflects the server without re-loading the page.
  const { fetchBooking } = useGetReservableBooking({ channelId });
  const [patchedBookings, setPatchedBookings] = useState<
    Record<string, ReservableBookingItem>
  >({});

  const refreshBooking = useCallback(
    async (interactionId: string) => {
      try {
        const booking = await fetchBooking(interactionId);

        if (booking) {
          setPatchedBookings(previous => ({
            ...previous,
            [booking.id]: booking,
          }));
        }
      } catch (error) {
        // The status change itself succeeded, so there is nothing to tell the
        // user: the row keeps the status it has until the next list fetch.
        Sentry.captureException(error, {
          contexts: { params: { channelId, interactionId } },
        });
      }
    },
    [fetchBooking, channelId]
  );

  const { columns } = useColumnsHeaders({
    channelTimeZone,
    noteStyles,
    statusRenderer: ReservableStatusDropdown,
    contentNameCellRenderer: ContentNameCell,
    onStatusUpdate: isStatusRefreshEnabled ? refreshBooking : undefined,
  });
  const { rowActions, error: rowActionError } = useRowActions();
  const exportOptions = useExportOptions(() => {
    downloadCSVExport({ keyword, dateRange: getDateRange(filters) });
  });

  useEffect(() => {
    if (!hasInitialQueryParamsLoaded) {
      return;
    }

    const { startDate, endDate } = getDateRange(filters);
    const startDateISO = startDate.toISOString();
    const endDateISO = endDate.toISOString();

    updateQueryParams({
      search: keyword,
      startDate: startDateISO,
      endDate: endDateISO,
    });
  }, [hasInitialQueryParamsLoaded, updateQueryParams, filters, keyword]);

  useEffect(() => {
    if (!hasInitialQueryParamsLoaded) {
      return;
    }

    const { search, startDate, endDate } = initialQueryParams;

    if (search) {
      setKeyword(search);
    }

    if (startDate && endDate) {
      setFilters([
        {
          id: DATE_RANGE_FILTER_ID,
          value: {
            startDate: new Date(startDate),
            endDate: new Date(endDate),
          },
        },
      ]);
    }
  }, [hasInitialQueryParamsLoaded, initialQueryParams]);

  useEffect(() => {
    fetchBookings({
      keyword,
      dateRange: getDateRange(filters),
      pagination: { start: page * perPage, perPage },
      sort,
    });
  }, [page, perPage, keyword, filters, sort]);

  // Whatever the list query returns is authoritative, so patches are dropped
  // once fresh rows arrive. `bookings` is a new array on every render while the
  // query has no data, so this must not set state when there is nothing to
  // clear - returning the previous value lets React bail out of the re-render.
  useEffect(() => {
    setPatchedBookings(previous =>
      Object.keys(previous).length > 0 ? {} : previous
    );
  }, [bookings]);

  if (getBookingsError || downloadCSVError) {
    window.Toast.show(<Text>{t(TRANSLATION_KEYS.serverError)}</Text>);

    return null;
  }

  if (rowActionError) {
    window.Toast.show(<Text>{t(TRANSLATION_KEYS.channelError)}</Text>);

    return null;
  }

  const handleKeywordFilterChange = (keyword: string) => {
    setKeyword(keyword);
    setPage(DEFAULT_PAGE_START);
  };

  const handleFilterChange = (filters: ColumnFiltersState) => {
    setFilters(filters);
    setPage(DEFAULT_PAGE_START);
  };

  const getEmptyMessage = () => {
    const { startDate, endDate } = getDateRange(filters);
    const format = (date: Date) =>
      DateTime.fromJSDate(date).toFormat('MMMM d, yyyy');

    return t(TRANSLATION_KEYS.emptyMessage, {
      startDate: format(startDate),
      endDate: format(endDate),
    });
  };

  if (!hasInitialQueryParamsLoaded) {
    return null;
  }

  return (
    <Table<RowData>
      isLoading={isLoading}
      columns={columns}
      data={bookingItemToRowDataMapper(
        t,
        channelTimeZone,
        isStatusRefreshEnabled
          ? bookings.map(booking => patchedBookings[booking.id] ?? booking)
          : bookings
      )}
      pagination="server"
      onPageChange={setPage}
      onPageSizeChange={setPerPage}
      page={page}
      pageSize={perPage}
      totalRows={totalBookings}
      rowActions={rowActions}
      hasKeywordFilter
      keywordFilter={initialQueryParams.search || ''}
      activeFilters={
        initialQueryParams.startDate && initialQueryParams.endDate
          ? [
              {
                id: DATE_RANGE_FILTER_ID,
                value: {
                  startDate: new Date(initialQueryParams.startDate),
                  endDate: new Date(initialQueryParams.endDate),
                },
              },
            ]
          : []
      }
      onKeywordFilterChange={handleKeywordFilterChange}
      onFilterChange={handleFilterChange}
      emptyMessage={getEmptyMessage()}
      exportOptions={exportOptions}
      tableKey="reservableManagement.reservableTable"
      filters={[
        {
          key: DATE_RANGE_FILTER_ID,
          type: NativeFilterTypes.DateRange,
          label: t(TRANSLATION_KEYS.dateRangeFilterLabel),
          isPromoted: true,
        },
      ]}
      onSortChange={setSort}
      sorting={sort}
    />
  );
}
